import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Dayjs } from 'dayjs';

interface FilterState {
    selectedTenantId: number | string | null; // null = all tenants, number = specific tenant
    searchText: string;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    statusFilters: string[];
    dateRange: [Dayjs, Dayjs] | null;
    customFilters: Record<string, any>;
}

interface FilterContextType {
    filters: FilterState;
    setSelectedTenantId: (tenantId: number | string | null) => void;
    setSearchText: (text: string) => void;
    setSortBy: (sortBy: string, sortOrder?: 'asc' | 'desc') => void;
    setStatusFilters: (statuses: string[]) => void;
    setDateRange: (range: [Dayjs, Dayjs] | null) => void;
    setCustomFilter: (key: string, value: any) => void;
    clearFilters: () => void;
    buildQueryParams: () => Record<string, any>;
}

const defaultFilterState: FilterState = {
    selectedTenantId: null, // null means "All Tenants"
    searchText: '',
    sortBy: 'name',
    sortOrder: 'asc',
    statusFilters: [],
    dateRange: null,
    customFilters: {},
};

const FilterContext = createContext<FilterContextType | undefined>(undefined);

export const useFilter = () => {
    const context = useContext(FilterContext);
    if (context === undefined) {
        throw new Error('useFilter must be used within a FilterProvider');
    }
    return context;
};

interface FilterProviderProps {
    children: ReactNode;
}

export const FilterProvider: React.FC<FilterProviderProps> = ({ children }) => {
    const [filters, setFilters] = useState<FilterState>(() => {
        // Load filters from localStorage on initialization
        try {
            const savedFilters = localStorage.getItem('superAdminFilters');
            if (savedFilters) {
                const parsed = JSON.parse(savedFilters);
                return {
                    ...defaultFilterState,
                    ...parsed,
                    dateRange: null, // Don't persist date range
                };
            }
        } catch (error) {
            console.error('Error loading saved filters:', error);
        }
        return defaultFilterState;
    });

    // Persist filters to localStorage whenever they change
    useEffect(() => {
        try {
            const filtersToSave = {
                ...filters,
                dateRange: null, // Don't persist date range
            };
            localStorage.setItem('superAdminFilters', JSON.stringify(filtersToSave));
        } catch (error) {
            console.error('Error saving filters:', error);
        }
    }, [filters]);

    const setSelectedTenantId = (tenantId: number | string | null) => {
        setFilters(prev => ({ ...prev, selectedTenantId: tenantId }));
    };

    const setSearchText = (text: string) => {
        setFilters(prev => ({ ...prev, searchText: text }));
    };

    const setSortBy = (sortBy: string, sortOrder: 'asc' | 'desc' = 'asc') => {
        setFilters(prev => ({ ...prev, sortBy, sortOrder }));
    };

    const setStatusFilters = (statuses: string[]) => {
        setFilters(prev => ({ ...prev, statusFilters: statuses }));
    };

    const setDateRange = (range: [Dayjs, Dayjs] | null) => {
        setFilters(prev => ({ ...prev, dateRange: range }));
    };

    const setCustomFilter = (key: string, value: any) => {
        setFilters(prev => ({
            ...prev,
            customFilters: { ...prev.customFilters, [key]: value }
        }));
    };

    const clearFilters = () => {
        setFilters(defaultFilterState);
        localStorage.removeItem('superAdminFilters');
    };

    const buildQueryParams = (): Record<string, any> => {
        const params: Record<string, any> = {};

        // Add tenant_id if a specific tenant is selected
        if (filters.selectedTenantId && filters.selectedTenantId !== 'all') {
            params.tenant_id = filters.selectedTenantId;
        }

        // Add search text
        if (filters.searchText) {
            params.search = filters.searchText;
        }

        // Add sorting
        if (filters.sortBy) {
            params.sortBy = filters.sortBy;
            params.sortOrder = filters.sortOrder;
        }

        // Add status filters
        if (filters.statusFilters.length > 0) {
            params.status = filters.statusFilters.join(',');
        }

        // Add date range
        if (filters.dateRange) {
            params.startDate = filters.dateRange[0].format('YYYY-MM-DD');
            params.endDate = filters.dateRange[1].format('YYYY-MM-DD');
        }

        // Add custom filters
        Object.keys(filters.customFilters).forEach(key => {
            if (filters.customFilters[key] !== undefined && filters.customFilters[key] !== null) {
                params[key] = filters.customFilters[key];
            }
        });

        return params;
    };

    const value: FilterContextType = {
        filters,
        setSelectedTenantId,
        setSearchText,
        setSortBy,
        setStatusFilters,
        setDateRange,
        setCustomFilter,
        clearFilters,
        buildQueryParams,
    };

    return (
        <FilterContext.Provider value={value}>
            {children}
        </FilterContext.Provider>
    );
};
